<script setup lang="ts">
	import { computed, ref, watch } from 'vue'
	import type { PolicyEvaluation } from '../composables/usePolicyEvaluator'

	interface Props {
		evaluations: PolicyEvaluation[]
		variant?: 'compact' | 'full'
		showCompliant?: boolean
	}

	interface Emits {
		(e: 'acceptanceChange', complete: boolean): void
	}

	const props = withDefaults(defineProps<Props>(), {
		variant: 'full',
		showCompliant: true,
	})
	const emit = defineEmits<Emits>()

	const violations = computed(() => props.evaluations.filter(e => e.violated))
	const hardViolations = computed(() => violations.value.filter(e => !e.policy.isFlexible))
	const softViolations = computed(() => violations.value.filter(e => e.policy.isFlexible))
	const compliant = computed(() => props.evaluations.filter(e => !e.violated))
	const hasViolations = computed(() => violations.value.length > 0)

	const acceptedPolicies = ref<Set<number>>(new Set())

	// Poda las aceptaciones a las flexibles que SIGUEN violadas: si una deja de
	// violarse (cambió la tarifa/fechas), su id no debe quedar acumulado en el Set.
	watch(softViolations, (current) => {
		const vigentes = new Set(current.map(e => e.policy.id))
		acceptedPolicies.value.forEach((id) => {
			if (! vigentes.has(id)) acceptedPolicies.value.delete(id)
		})
	})

	const allSoftAccepted = computed(() =>
		softViolations.value.every(e => acceptedPolicies.value.has(e.policy.id))
	)

	const allFlexibleViolationsAccepted = computed(() =>
		softViolations.value.length === 0 || allSoftAccepted.value
	)

	watch(allFlexibleViolationsAccepted, (val) => {
		emit('acceptanceChange', val && hardViolations.value.length === 0)
	}, { immediate: true })

	const toggleAcceptance = (id: number) => {
		if (acceptedPolicies.value.has(id)) {
			acceptedPolicies.value.delete(id)
		} else {
			acceptedPolicies.value.add(id)
		}
		emit('acceptanceChange', allFlexibleViolationsAccepted.value && hardViolations.value.length === 0)
	}

	const formatValue = (evaluation: PolicyEvaluation): string => {
		const { currentValue, limitInfo } = evaluation
		if (currentValue === null) return '—'
		const { unit } = limitInfo
		if (unit === 'MXN' || unit === 'MXN/noche') {
			return `$${Number(currentValue).toLocaleString('es-MX')} ${unit}`
		}
		return `${currentValue} ${unit}`
	}

	const formatLimit = (evaluation: PolicyEvaluation): string => {
		const { limitInfo } = evaluation
		const { min, max, unit } = limitInfo
		if (unit === 'MXN' || unit === 'MXN/noche') {
			if (max !== undefined) return `máx. $${Number(max).toLocaleString('es-MX')} ${unit}`
			if (min !== undefined) return `mín. $${Number(min).toLocaleString('es-MX')} ${unit}`
		}
		if (max !== undefined && min !== undefined) return `${min}–${max} ${unit}`
		if (max !== undefined) return `máx. ${max} ${unit}`
		if (min !== undefined) return `mín. ${min} ${unit}`
		return '—'
	}

	defineExpose({ allFlexibleViolationsAccepted, acceptedPolicies })
</script>

<template>
	<!-- Compact variant: small badges in hotel list -->
	<div
		v-if="variant === 'compact'"
		class="flex flex-wrap gap-1.5"
	>
		<template
			v-for="evaluation in violations"
			:key="evaluation.policy.id"
		>
			<span
				:class="[
					'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset',
					evaluation.policy.isFlexible
						? 'bg-amber-50 text-amber-800 ring-amber-200'
						: 'bg-red-50 text-red-800 ring-red-200',
				]"
				v-tooltip="`${evaluation.policy.displayName}\n${evaluation.policy.description}\n${evaluation.policy.isFlexible ? '⚠ Flexible: puede continuar con autorización' : '✗ Estricta: no se puede continuar'}`"
			>
				<span class="material-symbols-outlined text-[10px] leading-none">{{
					evaluation.policy.isFlexible ? 'warning' : 'block'
				}}</span>
				{{ evaluation.label }}
			</span>
		</template>
	</div>

	<!-- Full variant: detailed cards -->
	<div
		v-else-if="evaluations.length > 0"
		class="rounded-xl border border-gray-200 overflow-hidden shadow-sm"
	>
		<!-- Header -->
		<div class="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
			<div class="flex items-center gap-2">
				<div class="w-7 h-7 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
					<span class="material-symbols-outlined text-sm text-gray-600">policy</span>
				</div>
				<div>
					<p class="text-sm font-semibold text-gray-900">Políticas del grupo</p>
					<p class="text-xs text-gray-500">{{ evaluations.length }} política{{ evaluations.length !== 1 ? 's' : '' }} aplicable{{ evaluations.length !== 1 ? 's' : '' }}</p>
				</div>
			</div>
			<div class="flex items-center gap-1.5">
				<span
					v-if="hardViolations.length"
					class="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-800 ring-1 ring-inset ring-red-200"
				>
					<span class="material-symbols-outlined text-xs leading-none">block</span>
					{{ hardViolations.length }} bloqueante{{ hardViolations.length !== 1 ? 's' : '' }}
				</span>
				<span
					v-if="softViolations.length"
					class="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800 ring-1 ring-inset ring-amber-200"
				>
					<span class="material-symbols-outlined text-xs leading-none">warning</span>
					{{ softViolations.length }} flexible{{ softViolations.length !== 1 ? 's' : '' }}
				</span>
				<span
					v-if="!hasViolations"
					class="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800 ring-1 ring-inset ring-green-200"
				>
					<span class="material-symbols-outlined text-xs leading-none">check_circle</span>
					Cumple todo
				</span>
			</div>
		</div>

		<!-- Policy cards grid -->
		<div class="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
			<!-- Violated hard policies -->
			<div
				v-for="evaluation in hardViolations"
				:key="`hard-${evaluation.policy.id}`"
				class="relative rounded-lg border border-red-200 bg-red-50 p-3 overflow-hidden"
			>
				<div class="absolute left-0 top-0 bottom-0 w-1 bg-red-500 rounded-l-lg" />
				<div class="pl-2">
					<div class="flex items-start justify-between gap-2 mb-1.5">
						<div class="flex items-center gap-1.5 min-w-0">
							<span class="material-symbols-outlined text-base text-red-500 flex-shrink-0">block</span>
							<p class="text-sm font-semibold text-red-900 leading-tight">{{ evaluation.policy.displayName }}</p>
						</div>
						<span class="flex-shrink-0 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700 ring-1 ring-inset ring-red-200 whitespace-nowrap">
							Estricta
						</span>
					</div>
					<div class="flex items-center gap-2 text-xs mb-1.5">
						<span class="font-mono font-semibold text-red-800">{{ formatValue(evaluation) }}</span>
						<span class="text-red-400">→</span>
						<span class="text-red-600">{{ formatLimit(evaluation) }}</span>
					</div>
					<p class="text-xs text-red-700 leading-snug">No es posible continuar con esta reservación.</p>
				</div>
			</div>

			<!-- Violated flexible policies -->
			<div
				v-for="evaluation in softViolations"
				:key="`soft-${evaluation.policy.id}`"
				class="relative rounded-lg border border-amber-200 bg-amber-50 p-3 overflow-hidden"
			>
				<div class="absolute left-0 top-0 bottom-0 w-1 bg-amber-400 rounded-l-lg" />
				<div class="pl-2">
					<div class="flex items-start justify-between gap-2 mb-1.5">
						<div class="flex items-center gap-1.5 min-w-0">
							<span class="material-symbols-outlined text-base text-amber-500 flex-shrink-0">warning</span>
							<p class="text-sm font-semibold text-amber-900 leading-tight">{{ evaluation.policy.displayName }}</p>
						</div>
						<span class="flex-shrink-0 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-300 whitespace-nowrap">
							Flexible
						</span>
					</div>
					<div class="flex items-center gap-2 text-xs mb-1.5">
						<span class="font-mono font-semibold text-amber-800">{{ formatValue(evaluation) }}</span>
						<span class="text-amber-400">→</span>
						<span class="text-amber-700">{{ formatLimit(evaluation) }}</span>
					</div>
					<!-- Acceptance checkbox -->
					<label
						class="flex items-start gap-2 mt-2 pt-2 border-t border-amber-200 cursor-pointer group"
					>
						<div
							:class="[
								'mt-0.5 flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors',
								acceptedPolicies.has(evaluation.policy.id)
									? 'bg-amber-500 border-amber-500'
									: 'bg-white border-amber-300 group-hover:border-amber-500',
							]"
							@click="toggleAcceptance(evaluation.policy.id)"
						>
							<span
								v-if="acceptedPolicies.has(evaluation.policy.id)"
								class="material-symbols-outlined text-[10px] text-white leading-none"
							>check</span>
						</div>
						<span
							class="text-xs text-amber-800 leading-snug"
							@click="toggleAcceptance(evaluation.policy.id)"
						>
							Acepto continuar. Se notificará a mi superior.
						</span>
					</label>
				</div>
			</div>

			<!-- Compliant policies -->
			<template v-if="showCompliant">
				<div
					v-for="evaluation in compliant"
					:key="`ok-${evaluation.policy.id}`"
					class="relative rounded-lg border border-green-200 bg-green-50 p-3 overflow-hidden"
				>
					<div class="absolute left-0 top-0 bottom-0 w-1 bg-green-400 rounded-l-lg" />
					<div class="pl-2">
						<div class="flex items-start justify-between gap-2 mb-1.5">
							<div class="flex items-center gap-1.5 min-w-0">
								<span class="material-symbols-outlined text-base text-green-500 flex-shrink-0">check_circle</span>
								<p class="text-sm font-semibold text-green-900 leading-tight">{{ evaluation.policy.displayName }}</p>
							</div>
							<span
								:class="[
									'flex-shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset whitespace-nowrap',
									evaluation.policy.isFlexible
										? 'bg-green-100 text-green-700 ring-green-200'
										: 'bg-green-100 text-green-700 ring-green-200',
								]"
							>{{ evaluation.policy.isFlexible ? 'Flexible' : 'Estricta' }}</span>
						</div>
						<div class="flex items-center gap-2 text-xs">
							<span class="font-mono font-semibold text-green-800">{{ formatValue(evaluation) }}</span>
							<span class="text-green-400">→</span>
							<span class="text-green-600">{{ formatLimit(evaluation) }}</span>
						</div>
					</div>
				</div>
			</template>
		</div>

		<!-- Footer warning for hard violations -->
		<div
			v-if="hardViolations.length"
			class="px-4 py-2.5 bg-red-50 border-t border-red-200 flex items-center gap-2"
		>
			<span class="material-symbols-outlined text-sm text-red-500">error</span>
			<p class="text-xs text-red-700 font-medium">
				Esta solicitud no puede continuar hasta resolver {{ hardViolations.length === 1 ? 'la política bloqueante' : 'las políticas bloqueantes' }}.
			</p>
		</div>

		<!-- Footer warning for flexible accepted -->
		<div
			v-else-if="softViolations.length && allSoftAccepted"
			class="px-4 py-2.5 bg-amber-50 border-t border-amber-200 flex items-center gap-2"
		>
			<span class="material-symbols-outlined text-sm text-amber-500">info</span>
			<p class="text-xs text-amber-700 font-medium">
				Puede continuar. Su superior será notificado al realizar la reservación.
			</p>
		</div>

		<!-- Footer reminder for flexible not yet accepted -->
		<div
			v-else-if="softViolations.length && !allSoftAccepted"
			class="px-4 py-2.5 bg-amber-50 border-t border-amber-200 flex items-center gap-2"
		>
			<span class="material-symbols-outlined text-sm text-amber-500">pending</span>
			<p class="text-xs text-amber-700 font-medium">
				Acepte {{ softViolations.length === 1 ? 'la política flexible' : 'las políticas flexibles' }} para continuar.
			</p>
		</div>
	</div>
</template>
